import React, { useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { subscriptionApi } from '../services/api';
import { hasCheckedPendingPremium, markPendingPremiumChecked } from '../utils/pendingPremiumCheck';
import { AdProfileOnboardingModal } from './AdProfileOnboardingModal';

// Vérifie une seule fois par session, juste après connexion, si le compte dépasse
const STORAGE_CAPACITY_CHECK_KEY = 'storage_capacity_checked';

export const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated || loading) return;
    if (sessionStorage.getItem(STORAGE_CAPACITY_CHECK_KEY)) return;
    if (location.pathname === '/account') return;

    sessionStorage.setItem(STORAGE_CAPACITY_CHECK_KEY, '1');

    subscriptionApi.getUsage()
      .then((response: any) => {
        const storage = response.data?.overages?.storage;
        if (storage && storage.effective_limit != null && storage.used > storage.effective_limit) {
          navigate('/account', { state: { activeTab: 'documents' }, replace: true });
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, loading]);

  useEffect(() => {
    if (!isAuthenticated || loading) return;
    if (!user?.email_verified_at) return;
    if (hasCheckedPendingPremium()) return;
    if (location.pathname === '/upgrade-plan') return;

    const pendingPlanId = user.subscription?.pending_plan_id;
    if (!pendingPlanId) return;

    markPendingPremiumChecked();
    navigate('/upgrade-plan', {
      state: {
        planId: pendingPlanId,
        billingPeriod: user.subscription?.pending_billing_period || 'monthly',
      },
      replace: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, loading, user]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        Chargement...
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // Vérifier si l'email est vérifié
  if (user && !user.email_verified_at) {
    return <Navigate to="/verify-email" state={{ email: user.email }} />;
  }

  return (
    <>
      {children}
      <AdProfileOnboardingModal />
    </>
  );
};
